/* eslint-disable no-bitwise */
import type { ScenarioIndex, ScenarioIndexItem, ScenarioPackage } from "@mobile/runtime/types";

import type { SupportLanguage, TargetLanguage } from "../appModel";
import type {
  LearningDomain,
  LearningLevel,
  SignedWebContentCatalog,
  WebContentArtifact,
  WebContentArtifactFile,
  WebContentCatalogEntry,
} from "./webContentTypes";

const CATALOG_PUBLIC_KEYS: Record<string, string> = {
  "remote-content-20260517-v1": "1lUNqTonm3OxiZ4HcvsXD89KSkPgQnC3PhVxQBktM9w=",
};
const LEVELS = new Set<LearningLevel>(["a1", "a2", "b1", "b2"]);
const DOMAINS = new Set<LearningDomain>([
  "social_interaction",
  "daily_life",
  "services_public_interactions",
  "communication_repair_problem_solving",
]);
const TARGET_LANGUAGES = new Set<TargetLanguage>(["ar", "en", "it", "tr"]);
const SUPPORT_LANGUAGES = new Set<SupportLanguage>(["ar", "en", "tr"]);
const MAX_CATALOG_ENTRIES = 1000;
const MAX_ARTIFACT_FILES = 600;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function canonicalizeJson(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalizeJson(item)).join(",")}]`;
  }
  const entries = Object.entries(value)
    .filter(([, entryValue]) => entryValue !== undefined)
    .sort(([left], [right]) => left.localeCompare(right));
  return `{${entries.map(([key, entryValue]) => (
    `${JSON.stringify(key)}:${canonicalizeJson(entryValue)}`
  )).join(",")}}`;
}

function decodeBase64(value: string): Uint8Array {
  const normalized = value.trim().replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(normalized);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function isSafeJsonPath(value: unknown): value is string {
  return typeof value === "string"
    && value.length > 0
    && value.endsWith(".json")
    && !value.startsWith("/")
    && !value.includes("..");
}

function normalizeScenario(value: unknown): ScenarioIndexItem | null {
  if (!isRecord(value)) {
    return null;
  }
  const order = Number(value.order);
  const scenarioId = String(value.scenarioId || "").trim();
  const title = String(value.title || "").trim();
  const microSkill = String(value.microSkill || "").trim();
  const file = String(value.file || "").trim();
  if (!Number.isFinite(order) || !scenarioId || !title || !microSkill || !isSafeJsonPath(file)) {
    return null;
  }
  const readCount = (key: string) => {
    const count = Number(value[key]);
    return Number.isFinite(count) && count >= 0 ? count : undefined;
  };
  return {
    order,
    scenarioId,
    title,
    microSkill,
    file,
    status: value.status ? String(value.status) : undefined,
    sentenceCount: readCount("sentenceCount"),
    vocabularyCount: readCount("vocabularyCount"),
    missionCount: readCount("missionCount"),
    previewCount: readCount("previewCount"),
  };
}

function normalizeEntry(value: unknown): WebContentCatalogEntry | null {
  if (!isRecord(value)) {
    return null;
  }
  const targetLanguage = String(value.targetLanguage || "") as TargetLanguage;
  const level = String(value.level || "").toLowerCase() as LearningLevel;
  const domain = String(value.domain || "") as LearningDomain;
  const packageId = String(value.packageId || "").trim();
  const indexPath = String(value.indexPath || "").trim();
  const scenarios = Array.isArray(value.scenarios)
    ? value.scenarios.map(normalizeScenario).filter((item): item is ScenarioIndexItem => Boolean(item))
    : [];
  const expectedPackageId = `${targetLanguage}.${level}.${domain}`;
  const expectedIndexPath = `content/runtime/${targetLanguage}/${level}/${domain}/index.json`;
  if (
    !TARGET_LANGUAGES.has(targetLanguage)
    || !LEVELS.has(level)
    || !DOMAINS.has(domain)
    || packageId !== expectedPackageId
    || indexPath !== expectedIndexPath
    || scenarios.length === 0
    || scenarios.length !== (value.scenarios as unknown[]).length
  ) {
    return null;
  }
  const positiveNumber = (key: string, allowZero = false) => {
    const number = Number(value[key]);
    return Number.isFinite(number) && (allowZero ? number >= 0 : number > 0) ? number : null;
  };
  const scenarioCount = positiveNumber("scenarioCount");
  const packageSchemaVersion = positiveNumber("packageSchemaVersion");
  const sentenceCount = positiveNumber("sentenceCount", true);
  const supportFileCount = positiveNumber("supportFileCount", true);
  const estimatedBytes = positiveNumber("estimatedBytes");
  const estimatedSizeMb = positiveNumber("estimatedSizeMb");
  const contentHash = String(value.contentHash || "").trim();
  if (
    scenarioCount !== scenarios.length
    || packageSchemaVersion === null
    || sentenceCount === null
    || supportFileCount === null
    || estimatedBytes === null
    || estimatedSizeMb === null
    || !contentHash
  ) {
    return null;
  }

  const supportScenariosByUiLanguage: Partial<Record<SupportLanguage, ScenarioIndexItem[]>> = {};
  if (isRecord(value.supportScenariosByUiLanguage)) {
    for (const [language, items] of Object.entries(value.supportScenariosByUiLanguage)) {
      if (!SUPPORT_LANGUAGES.has(language as SupportLanguage) || !Array.isArray(items)) {
        continue;
      }
      const normalized = items.map(normalizeScenario).filter((item): item is ScenarioIndexItem => Boolean(item));
      if (normalized.length === items.length) {
        supportScenariosByUiLanguage[language as SupportLanguage] = normalized;
      }
    }
  }

  return {
    targetLanguage,
    level,
    domain,
    packageId,
    indexPath,
    scenarioCount,
    packageSchemaVersion,
    contentHash,
    sentenceCount,
    supportFileCount,
    estimatedBytes,
    estimatedSizeMb,
    scenarios,
    supportScenariosByUiLanguage,
  };
}

export async function validateSignedWebContentCatalog(value: unknown): Promise<SignedWebContentCatalog> {
  if (!isRecord(value)) {
    throw new Error("content_catalog_invalid");
  }
  const catalogVersion = String(value.catalogVersion || "").trim();
  const keyId = String(value.keyId || "").trim();
  const signature = String(value.signature || "").trim();
  if (
    value.schemaVersion !== 1
    || value.contentType !== "domainContentDiscoveryCatalog"
    || !catalogVersion
    || !CATALOG_PUBLIC_KEYS[keyId]
    || !signature
    || !Array.isArray(value.entries)
    || value.entries.length === 0
    || value.entries.length > MAX_CATALOG_ENTRIES
  ) {
    throw new Error("content_catalog_invalid");
  }
  const entries = value.entries.map(normalizeEntry);
  if (entries.some((entry) => !entry)) {
    throw new Error("content_catalog_entry_invalid");
  }

  const unsigned = { ...value };
  delete unsigned.signature;
  const publicKeyBytes = new Uint8Array(decodeBase64(CATALOG_PUBLIC_KEYS[keyId])).buffer as ArrayBuffer;
  const signatureBytes = new Uint8Array(decodeBase64(signature)).buffer as ArrayBuffer;
  const publicKey = await crypto.subtle.importKey(
    "raw",
    publicKeyBytes,
    { name: "Ed25519" },
    false,
    ["verify"],
  );
  const verified = await crypto.subtle.verify(
    { name: "Ed25519" },
    publicKey,
    signatureBytes,
    new TextEncoder().encode(canonicalizeJson(unsigned)),
  );
  if (!verified) {
    throw new Error("content_catalog_signature_invalid");
  }
  return {
    schemaVersion: 1,
    contentType: "domainContentDiscoveryCatalog",
    catalogVersion,
    issuedAt: typeof value.issuedAt === "string" ? value.issuedAt : undefined,
    keyId,
    signature,
    entries: entries as WebContentCatalogEntry[],
  };
}

function updateStableHash(hash: number, text: string): number {
  let next = hash;
  for (const character of text) {
    next = (next * 33 + (character.codePointAt(0) || 0)) % 0xffffffff;
  }
  return next;
}

export function buildWebContentArtifactHash(files: WebContentArtifactFile[]): string {
  let hash = 5381;
  for (const file of files) {
    hash = updateStableHash(hash, file.path);
    hash = updateStableHash(hash, "\n");
    hash = updateStableHash(hash, JSON.stringify(file.payload));
    hash = updateStableHash(hash, "\n");
  }
  return hash.toString(16).padStart(8, "0");
}

function allowedArtifactPath(path: string, entry: WebContentCatalogEntry): boolean {
  if (path.startsWith(`content/runtime/${entry.targetLanguage}/${entry.level}/${entry.domain}/`)) {
    return true;
  }
  return [...SUPPORT_LANGUAGES].some((language) => (
    path.startsWith(`content/locale_overlays/runtime/${language}/${entry.targetLanguage}/${entry.level}/${entry.domain}/`)
    || path.startsWith(`content/translation_support/runtime/${language}/${entry.targetLanguage}/${entry.level}/${entry.domain}/`)
  ));
}

export function validateWebContentArtifact(value: unknown, entry: WebContentCatalogEntry): WebContentArtifact {
  if (!isRecord(value) || !Array.isArray(value.files)) {
    throw new Error("content_artifact_invalid");
  }
  if (
    value.contentType !== "domainContentPackage"
    || Number(value.schemaVersion) !== entry.packageSchemaVersion
    || value.packageId !== entry.packageId
    || value.targetLanguage !== entry.targetLanguage
    || String(value.level).toLowerCase() !== entry.level
    || value.domain !== entry.domain
    || value.contentHash !== entry.contentHash
    || value.files.length === 0
    || value.files.length > MAX_ARTIFACT_FILES
  ) {
    throw new Error("content_artifact_contract_mismatch");
  }
  const paths = new Set<string>();
  const files: WebContentArtifactFile[] = [];
  for (const file of value.files) {
    if (!isRecord(file) || !isSafeJsonPath(file.path) || !("payload" in file)) {
      throw new Error("content_artifact_file_invalid");
    }
    if (!allowedArtifactPath(file.path, entry) || paths.has(file.path)) {
      throw new Error("content_artifact_path_invalid");
    }
    paths.add(file.path);
    files.push({ path: file.path, payload: file.payload });
  }
  if (buildWebContentArtifactHash(files) !== entry.contentHash) {
    throw new Error("content_artifact_hash_mismatch");
  }
  const indexFile = files.find((file) => file.path === entry.indexPath);
  if (!indexFile || !isRecord(indexFile.payload) || !Array.isArray(indexFile.payload.scenarios)) {
    throw new Error("content_artifact_index_missing");
  }
  const index = indexFile.payload as unknown as ScenarioIndex;
  const baseDirectory = entry.indexPath.slice(0, entry.indexPath.lastIndexOf("/") + 1);
  for (const scenario of index.scenarios) {
    const path = `${baseDirectory}${scenario.file}`;
    const scenarioFile = files.find((file) => file.path === path);
    if (!scenarioFile || !isRecord(scenarioFile.payload)) {
      throw new Error("content_artifact_scenario_missing");
    }
    const payload = scenarioFile.payload as Partial<ScenarioPackage>;
    if (payload.scenarioId !== scenario.scenarioId || !Array.isArray(payload.sentenceLibrary)) {
      throw new Error("content_artifact_scenario_invalid");
    }
  }
  return {
    schemaVersion: entry.packageSchemaVersion,
    contentType: "domainContentPackage",
    packageId: entry.packageId,
    targetLanguage: entry.targetLanguage,
    level: entry.level,
    domain: entry.domain,
    contentHash: entry.contentHash,
    files,
  };
}
